/**
 * 去掉文件扩展名，如 test.png 转成 test，dir/test.png 转成 dir/test
 */
export const removeFileExtension = (filename: string): string => {
  return filename.replace(/\.\w+$/, '')
}

/**
 * getFileExtension
 * test.png 返回 png
 * test.dsdd.png 返回 png
 * test....png 返回 png
 * @param {string} url 文件名或者文件URL，test.png，返回 png
 * @param {boolean} [includeDot=false] 是否包含点号，默认不包含，如果为 true，返回的后缀名带 .
 */
export const getFileExtension = (
  url: string | undefined,
  includeDot = false,
): string => {
  if (!url) return ''
  // eslint-disable-next-line no-use-before-define
  const fileName = getFileName(url)
  const extList = fileName.split('.')
  const ext = extList[extList.length - 1]
  if (ext) {
    return includeDot ? `.${ext}` : ext
  }

  return ''
}

/**
 * 读取文件名字，如 resource/test.png 返回 test.png
 * @param url
 * @param removeExt 是否移除扩展名，默认不移除
 */
export const getFileName = (url: string, removeExt = false): string => {
  const hasOrigin = /^(?:http|https|file):\/\//.test(url)
  if (hasOrigin) {
    url = new URL(url).pathname
  }

  if (removeExt) {
    return removeFileExtension(url.slice(url.lastIndexOf('/') + 1))
  }
  return url.slice(url.lastIndexOf('/') + 1)
}

/**
 * 读取路径
 * @param url
 * @param removeExt 是否移除扩展名，默认不移除
 */
export const getFilePath = (url: string, removeExt = false): string => {
  const hasOrigin = /^(?:http|https|file):\/\//.test(url)
  if (hasOrigin) {
    url = new URL(url).pathname
  }

  if (removeExt) {
    return removeFileExtension(url)
  }
  return url
}

/**
 * isMp3
 * @param {string} url 文件名或者文件URL，test.mp3
 */
export const isMp3 = (url: string): boolean => {
  return /\.mp3$/i.test(url)
}

/**
 * isPng
 * @param {string} url 文件名或者文件URL，test.ong
 */
export const isPng = (url: string): boolean => {
  return /\.png$/i.test(url)
}

/**
 * isJpg
 * @param {string} url 文件名或者文件URL，test.jpg
 */
export const isJpg = (url: string): boolean => {
  return /\.jp(e)?g$/i.test(url)
}

/**
 * isMp4
 * @param {string} url 文件名或者文件URL，test.mp4
 */
export const isMp4 = (url: string): boolean => {
  return /\.mp4$/i.test(url)
}

/**
 * isZip
 * @param {string} url 文件名或者文件URL，test.zip
 */
export const isZip = (url: string): boolean => {
  return /\.zip$/i.test(url)
}

/**
 * isImage
 * @param {string} url 文件名或者文件URL，test.png
 */
export const isImage = (url: string): boolean => {
  return /(\.jp(e)?g|\.png|\.gif)$/i.test(url)
}

export function replaceJpegToJpg(name: string): string {
  return name.replace(/\.jpeg$/i, '.jpg')
}

export function convertJPEGtoJPG(jpegFile: File): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = function (event) {
      const image = new Image()
      image.onload = function () {
        const canvas = document.createElement('canvas')
        canvas.width = image.width
        canvas.height = image.height
        const ctx = canvas.getContext('2d')
        ctx?.drawImage(image, 0, 0)
        canvas.toBlob(
          function (blob) {
            const jpgFile = new File(
              [blob!],
              `${getFileName(jpegFile.name, true)}.jpg`,
              {
                type: 'image/jpg',
              },
            )
            resolve(jpgFile)
          },
          'image/jpg',
          1,
        )
      }
      image.onerror = function () {
        reject(new Error('Failed to load the image.'))
      }
      image.src = event.target?.result as any
    }
    reader.onerror = function () {
      reject(new Error('Failed to read the file.'))
    }
    reader.readAsDataURL(jpegFile)
  })
}

/**
 * 判断文件名是否合法，跟系统的要求一样
 * @param name
 */
export function isValidFileName(name: string): boolean {
  return /^[^/\\":*?<>|\t\n\r]{1,200}$/i.test(name)
}

// 是否视频资源并且包含关联音频
export function isVideoResourceAndHasAudio(resource: any): boolean {
  // 资源前缀包含video并且extra数据中存在audio_url
  return (
    resource.prefix.indexOf('video') > -1 &&
    Boolean(resource.extra.ext_audio_path)
  )
}
